import React, { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { Button } from '../ui/Button';
import { recruitmentTheme } from '../../styles/theme';
import { LogOut, User, Bell, Search, BellOff, X, CheckCheck } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LocalStorageManager } from '../../utils/localStorage';
import { enhancedSearch } from '../../utils/searchUtils';

export const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const { notifications, unreadCount, markAsRead, markAllAsRead, removeNotification } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Comprehensive search database
  const searchDatabase = useMemo(() => [
    // Pages and their content
    { keywords: ['dashboard', 'home', 'overview', 'main'], route: '/dashboard', title: 'Dashboard', description: 'Main dashboard with overview' },
    { keywords: ['candidates', 'candidate', 'people', 'applicants', 'workers'], route: '/candidates', title: 'Candidates', description: 'Manage candidates and applicants' },
    { keywords: ['cv builder', 'cv', 'resume', 'curriculum', 'profile', 'create cv'], route: '/cv-builder', title: 'CV Builder', description: 'Create and edit CVs' },
    { keywords: ['associates', 'associate', 'staff', 'employees', 'team'], route: '/associates', title: 'Associates', description: 'Associates dashboard and tasks' },
    { keywords: ['receptionist', 'reception', 'front desk'], route: '/receptionist-view', title: 'Receptionist View', description: 'Receptionist dashboard and activities' },
    { keywords: ['tasks', 'task', 'todo', 'assignments', 'work items'], route: '/tasks', title: 'Tasks', description: 'Task management and tracking' },
    { keywords: ['appointments', 'appointment', 'meetings', 'interviews', 'schedule'], route: '/appointments', title: 'Appointments', description: 'Schedule and manage appointments' },
    { keywords: ['jobs', 'job', 'positions', 'openings', 'vacancies'], route: '/jobs', title: 'Jobs', description: 'Available job positions' },
    { keywords: ['job generator', 'generator', 'create job', 'job posting'], route: '/job-generator', title: 'Job Generator', description: 'Create new job postings' },
    { keywords: ['documents', 'document', 'files', 'contracts', 'licenses', 'certificates'], route: '/documents', title: 'Documents', description: 'Document management system' },
    { keywords: ['reports', 'report', 'analytics', 'statistics', 'metrics'], route: '/reports', title: 'Reports', description: 'Analytics and reporting' },
    { keywords: ['user management', 'users', 'user', 'admin', 'permissions'], route: '/settings', title: 'User Management (Settings)', description: 'Manage users and permissions in Settings' },
    { keywords: ['settings', 'setting', 'configuration', 'preferences'], route: '/settings', title: 'Settings', description: 'Application settings' },
    
    // Status and actions
    { keywords: ['active', 'status', 'hired', 'interviewing', 'onboarding'], route: '/candidates', title: 'Candidate Status', description: 'Filter candidates by status' },
    { keywords: ['pending tasks', 'completed tasks', 'overdue'], route: '/tasks', title: 'Task Status', description: 'Filter tasks by status' },
    { keywords: ['confirmed appointments', 'cancelled', 'rescheduled'], route: '/appointments', title: 'Appointment Status', description: 'Filter appointments by status' },
    
    // Features and functionality
    { keywords: ['upload', 'download', 'export', 'import'], route: '/documents', title: 'File Management', description: 'Upload and download files' },
    { keywords: ['search', 'filter', 'sort'], route: '/candidates', title: 'Search & Filter', description: 'Search and filter candidates' },
    { keywords: ['notifications', 'alerts', 'updates'], route: '/dashboard', title: 'Notifications', description: 'View notifications and alerts' }
  ], []);

  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const term = searchTerm.toLowerCase();
    const results = [];
    
    // Search in navigation/pages
    const pageResults = searchDatabase.filter(item => 
      item.keywords.some(keyword => keyword.includes(term)) ||
      item.title.toLowerCase().includes(term) ||
      item.description.toLowerCase().includes(term)
    );
    results.push(...pageResults);
    
    // Enhanced search function for partial matches (like phone numbers)
    const performEnhancedSearch = (searchTerm: string, searchableFields: string[]): boolean => {
      const cleanedTerm = searchTerm.toLowerCase().replace(/[-\s()]/g, '');
      
      return searchableFields.some(field => {
        if (!field) return false;
        
        const fieldStr = field.toString().toLowerCase();
        const cleanedField = fieldStr.replace(/[-\s()]/g, '');
        
        // Direct match
        if (fieldStr.includes(searchTerm.toLowerCase())) return true;
        
        // Partial phone number match (remove formatting)
        if (cleanedField.includes(cleanedTerm)) return true;
        
        // Enhanced search for better matching
        return enhancedSearch(searchTerm, [field]);
      });
    };
    
    // Search candidates using LocalStorageManager
    try {
      const allCandidates = LocalStorageManager.getCandidates();
      const candidateResults = allCandidates.filter(candidate => {
        const searchableFields = [
          candidate.name || '',
          candidate.email || '',
          candidate.phone || '',
          candidate.jobTitle || '',
          candidate.company || '',
          candidate.countryApplyingTo || '',
          candidate.passportNumber || '',
          candidate.stage || '',
          candidate.city || '',
          candidate.notes || '',
          candidate.fullNameArabic || '',
          candidate.address || '',
          candidate.nationality || '',
          candidate.religion || '',
          candidate.educationLevel || '',
          candidate.englishLevel || '',
          candidate.arabicLevel || '',
          candidate.workPosition || '',
          candidate.workCountry || '',
          candidate.skills?.join(' ') || '',
          candidate.remarks || '',
          (candidate.salary || '').toString(),
          candidate.id || '',
          // Remove formatting from phone for partial matching
          (candidate.phone || '').replace(/[-\s()]/g, '')
        ];
        
        return performEnhancedSearch(term, searchableFields);
      }).slice(0, 3).map(candidate => ({
        keywords: [],
        route: '/candidates',
        title: candidate.name,
        description: `${candidate.phone || ''} ${candidate.passportNumber ? '• ' + candidate.passportNumber : ''} • ${candidate.jobTitle || 'N/A'} • ${candidate.stage} • ${candidate.countryApplyingTo || 'N/A'}`
      }));
      results.push(...candidateResults);
    } catch (error) {
      console.error('Error searching candidates:', error);
    }

    // Search appointments using LocalStorageManager
    try {
      const allAppointments = LocalStorageManager.getAppointments();
      const appointmentResults = allAppointments.filter(appointment => {
        const searchableFields = [
          appointment.candidateName || '',
          appointment.appointmentType || '',
          appointment.location || '',
          appointment.candidatePhone || '',
          appointment.status || '',
          appointment.interviewer || '',
          appointment.date || '',
          appointment.time || '',
          appointment.notes || '',
          appointment.candidateStage || '',
          appointment.candidateEmail || '',
          appointment.id || ''
        ];
        
        return performEnhancedSearch(term, searchableFields);
      }).slice(0, 3).map(appointment => ({
        keywords: [],
        route: '/appointments',
        title: `${appointment.candidateName} - ${appointment.appointmentType}`,
        description: `${appointment.date} at ${appointment.time} • ${appointment.location} • ${appointment.status}`
      }));
      results.push(...appointmentResults);
    } catch (error) {
      console.error('Error searching appointments:', error);
    }

    // Search jobs using LocalStorageManager
    try {
      const allJobs = LocalStorageManager.getJobs();
      const jobResults = allJobs.filter(job => {
        const searchableFields = [
          job.title || '',
          job.company || '',
          job.location || '',
          job.status || '',
          job.description || '',
          (job.requirements || []).join(' '),
          (job.benefits || []).join(' '),
          (job.positions || []).join(' '),
          (job.gender || []).join(' '),
          job.city || '',
          job.country || '',
          job.experience || '',
          job.accommodation || '',
          job.additionalDetails || '',
          `${job.salaryRange?.min || ''} ${job.salaryRange?.max || ''} ${job.salaryRange?.currency || ''}`,
          job.id || ''
        ];
        
        return performEnhancedSearch(term, searchableFields);
      }).slice(0, 3).map(job => ({
        keywords: [],
        route: '/jobs',
        title: job.title,
        description: `${job.company} • ${job.location} • ${job.salaryRange?.min || 'N/A'}-${job.salaryRange?.max || 'N/A'} ${job.salaryRange?.currency || 'KES'}`
      }));
      results.push(...jobResults);
    } catch (error) {
      console.error('Error searching jobs:', error);
    }
    
    return results.slice(0, 10); // Show top 10 results
  }, [searchTerm, searchDatabase]);

  const handleGlobalSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    // Get the best match
    const bestMatch = searchResults[0];
    if (bestMatch) {
      navigate(bestMatch.route);
      setSearchTerm('');
      setShowSearchResults(false);
    }
  };

  const handleSearchResultClick = (route: string) => {
    navigate(route);
    setSearchTerm('');
    setShowSearchResults(false);
  };

  // Filter notifications based on user preferences
  const filteredNotifications = notifications.filter(notification => {
    switch (notification.type) {
      case 'badge':
        return settings.notifications.emailNotifications; // Badge notifications grouped with email
      case 'task':
      case 'overdue':
      case 'priority':
        return settings.notifications.taskReminders;
      case 'candidate':
        return settings.notifications.candidateUpdates;
      case 'appointment':
        return true; // Always show appointment notifications
      default:
        return true;
    }
  });

  return (
    <header className={recruitmentTheme.components.nav.header}>
      <div className="flex items-center justify-between px-3 sm:px-4 md:px-6 py-3 md:py-4">
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-[#8b6914]">
            {user?.role} Dashboard
          </h2>
        </div>
        
        <div className="flex items-center space-x-2 md:space-x-4">
          {/* Global Search */}
          <div className="relative">
            <form onSubmit={handleGlobalSearch}>
              <div className="flex items-center">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowSearchResults(e.target.value.trim().length > 0);
                  }}
                  onFocus={() => setShowSearchResults(searchTerm.trim().length > 0)}
                  onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                  placeholder="Search..."
                  className="w-48 sm:w-60 md:w-80 px-3 py-2 pr-10 text-sm border border-[#b8860b]/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#b8860b]/50 bg-white"
                  data-search="global"
                />
                <button
                  type="submit"
                  className="absolute right-2 p-1 text-[#8b6914] hover:text-[#b8860b] transition-colors"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </form>

            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#b8860b]/30 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    onClick={() => handleSearchResultClick(result.route)}
                    className="p-3 hover:bg-[#f0e4cc]/20 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-center space-x-2">
                      <Search className="w-4 h-4 text-[#8b6914] flex-shrink-0" />
                      <div>
                        <p className="font-medium text-[#8b6914] text-sm">{result.title}</p>
                        <p className="text-xs text-gray-600">{result.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notification Bell */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-[#8b6914] dark:text-[#FFD700] hover:text-[#b8860b] dark:hover:text-[#FFD700]/80 transition-colors"
              title={filteredNotifications.length === 0 ? "No notifications (some may be disabled in settings)" : `${unreadCount} unread notifications`}
            >
              {filteredNotifications.length === 0 && notifications.length > 0 ? (
                <BellOff className="w-5 h-5" />
              ) : (
                <Bell className="w-5 h-5" />
              )}
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs font-bold text-white flex items-center justify-center border-2 border-white shadow-lg">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-white dark:bg-gray-800 border border-[#b8860b]/30 dark:border-gray-600 rounded-lg shadow-lg z-50">
                <div className="p-3 border-b border-[#b8860b]/20 dark:border-gray-600 flex items-center justify-between">
                  <h3 className="font-semibold text-[#8b6914] dark:text-[#FFD700]">Notifications</h3>
                  {filteredNotifications.length > 0 && (
                    <button
                      onClick={() => {
                        markAllAsRead();
                        setShowNotifications(false);
                      }}
                      className="text-xs text-[#8b6914] dark:text-[#FFD700] hover:text-[#b8860b] dark:hover:text-[#FFD700]/80 flex items-center space-x-1"
                    >
                      <CheckCheck className="w-3 h-3" />
                      <span>Mark all read</span>
                    </button>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {filteredNotifications.length === 0 ? (
                    <div className="p-3 text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {notifications.length === 0 
                          ? "No notifications available" 
                          : "All notifications are disabled in settings"
                        }
                      </p>
                      {notifications.length > 0 && (
                        <button
                          onClick={() => {
                            navigate('/settings');
                            setShowNotifications(false);
                          }}
                          className="text-xs text-[#8b6914] dark:text-[#FFD700] hover:text-[#b8860b] dark:hover:text-[#FFD700]/80 mt-2 underline"
                        >
                          Enable in Settings
                        </button>
                      )}
                    </div>
                  ) : (
                    filteredNotifications.map((notification) => (
                      <div 
                        key={notification.id} 
                        className={`p-3 border-b border-gray-100 dark:border-gray-600 last:border-b-0 hover:bg-[#f0e4cc]/20 dark:hover:bg-gray-700 cursor-pointer group ${
                          !notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                        onClick={() => markAsRead(notification.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className={`text-sm ${!notification.read ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-800 dark:text-gray-200'}`}>
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{notification.time}</p>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                                notification.priority === 'high' ? 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300' :
                                notification.priority === 'medium' ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-300' :
                                'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                                {notification.priority}
                              </span>
                              <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                                notification.type === 'task' || notification.type === 'overdue' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' :
                                notification.type === 'candidate' ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300' :
                                notification.type === 'appointment' ? 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300' :
                                notification.type === 'badge' ? 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300' :
                                notification.type === 'success' ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300' :
                                notification.type === 'warning' ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-300' :
                                notification.type === 'error' ? 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300' :
                                'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                                {notification.type}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeNotification(notification.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 ml-2 p-1 text-gray-400 hover:text-red-500 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-2 border-t border-[#b8860b]/20 dark:border-gray-600">
                  <button
                    onClick={() => setShowNotifications(false)}
                    className="w-full text-sm text-[#8b6914] dark:text-[#FFD700] hover:text-[#b8860b] dark:hover:text-[#FFD700]/80 py-1"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="hidden lg:flex items-center space-x-2 text-sm text-[#8b6914]">
            <User className="w-4 h-4" />
            <span className="truncate max-w-32">{user?.email}</span>
          </div>
          
          <button
            onClick={logout}
            className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 lg:space-x-2 px-2 lg:px-3`}
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};
